/**
 * Report Mode View
 * Full-screen report writing mode with embedded DICOM viewer and report form
 * Sidebar is minimized, viewer shows on left, report fields on right
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, IconButton, Typography, Tooltip, Paper, Grid,
  FormControl, FormLabel, Button, FormHelperText, Stack, Divider,
  OutlinedInput, Alert, Chip, Collapse, Dialog, DialogContent, DialogTitle
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { getReportRequest, postReportRequest, putReportRequest, resetGetReport, resetPostReport } from '../../redux/modules/report/slice';
import { getStaffList } from '../../redux/modules/staff/slice';
import { toast } from 'react-toastify';
import SelectStaff from '../../pages/adminDashboard/radiology/form/SelectStaff';
import DictationTextField from '../DictationTextField';
import OHIFViewer from '../OHIFViewer';
import TemplateSelector from '../TemplateSelector';

const validationSchema = yup.object().shape({
  diagnosticObjectives: yup.string(),
  findings: yup.string(),
  radiographicImpression: yup.string(),
  appointerId: yup.string(),
  appointerSignaturePin: yup.string(),
  reportStatus: yup.string(),
});

const ReportModeView = ({ itemData, onExit, onSubmitComplete }) => {
  const dispatch = useDispatch();
  const { loading, getReportRes, postReportRes, putReportRes } = useSelector(state => state.report);
  const { currentBranch } = useSelector(state => state.branch);
  const [obMeasurements, setObMeasurements] = useState(null);
  const [showOBAlert, setShowOBAlert] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const studyInstanceUID = itemData?.studyData?.orthancStudyInstanceUID;
  const [worksheetExpanded, setWorksheetExpanded] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfDialogUrl, setPdfDialogUrl] = useState('');
  const [pdfDialogTitle, setPdfDialogTitle] = useState('');
  const [pdfDialogType, setPdfDialogType] = useState('pdf');

  // Server API URL
  const serverApiUrl = process.env.REACT_APP_SERVER_API || 'http://localhost:9000/';
  const getPdfUrl = (instanceId) => `${serverApiUrl}api/dicom/orthanc/instances/${instanceId}/pdf`;
  const getPreviewUrl = (instanceId) => `${serverApiUrl}api/dicom/orthanc/instances/${instanceId}/rendered`;

  // Get worksheet data from order
  const encapsulatedPDFs = useMemo(() => {
    return itemData?.studyData?.encapsulatedPDFs || [];
  }, [itemData]);

  const dicomComments = useMemo(() => {
    return itemData?.studyData?.dicomComments || null;
  }, [itemData]);

  const structuredReports = useMemo(() => {
    return itemData?.studyData?.structuredReports || [];
  }, [itemData]);

  const secondaryCaptureInstances = useMemo(() => {
    return itemData?.studyData?.secondaryCaptureInstances || [];
  }, [itemData]);

  const hasPdfs = encapsulatedPDFs.length > 0;
  const hasComments = dicomComments?.studyComments || dicomComments?.imageComments?.length > 0;
  const hasSRs = structuredReports.length > 0;
  const hasSCs = secondaryCaptureInstances.length > 0;
  const hasAnyWorksheetContent = hasPdfs || hasComments || hasSRs || hasSCs;

  const getAuthHeaders = () => {
    const authToken = localStorage.getItem('item') || localStorage.getItem('token');
    const headers = {};

    if (authToken) {
      headers.Authorization = authToken;
    }

    const currentBranchRaw = localStorage.getItem('currentBranch');
    if (currentBranchRaw) {
      try {
        const currentBranchData = JSON.parse(currentBranchRaw);
        if (currentBranchData?.id) {
          headers['X-Current-Branch'] = currentBranchData.id;
        }
      } catch (e) {
        // Ignore malformed branch metadata
      }
    }

    return headers;
  };

  // Fetch SC images with auth and cache as blob URLs
  const [scImageUrls, setScImageUrls] = useState({});
  useEffect(() => {
    if (!hasSCs) {
      return;
    }

    const headers = getAuthHeaders();
    if (!headers.Authorization) {
      return;
    }

    let cancelled = false;
    let previousUrls = [];
    const objectUrls = [];

    const loadImages = async () => {
      const urls = {};
      for (const sc of secondaryCaptureInstances) {
        if (!sc.instanceId) continue;
        try {
          const res = await fetch(getPreviewUrl(sc.instanceId), {
            headers
          });
          if (!res.ok) continue;
          const blob = await res.blob();
          if (cancelled) return;
          const objectUrl = URL.createObjectURL(blob);
          objectUrls.push(objectUrl);
          urls[sc.instanceId] = objectUrl;
        } catch (e) {
          // skip failed images
        }
      }
      if (!cancelled) {
        setScImageUrls((prevUrls) => {
          previousUrls = Object.values(prevUrls);
          return urls;
        });
      }
    };
    loadImages();

    return () => {
      cancelled = true;
      previousUrls.forEach((url) => URL.revokeObjectURL(url));
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [secondaryCaptureInstances, hasSCs]);

  // Open PDF in popup dialog
  const handleOpenPdfDialog = async (instanceId, title) => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(getPdfUrl(instanceId), { headers });
      if (!response.ok) {
        throw new Error('Failed to load worksheet PDF');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfDialogUrl(blobUrl);
      setPdfDialogType('pdf');
      setPdfDialogTitle(title || 'Sonographer Worksheet');
      setPdfDialogOpen(true);
    } catch (error) {
      console.error('PDF open error:', error);
      toast.error('Unable to open worksheet PDF');
    }
  };

  const handleOpenScDialog = async (instanceId, title) => {
    if (!instanceId) return;

    if (scImageUrls[instanceId]) {
      setPdfDialogUrl(scImageUrls[instanceId]);
      setPdfDialogType('image');
      setPdfDialogTitle(title || 'Measurement Image');
      setPdfDialogOpen(true);
      return;
    }

    try {
      const headers = getAuthHeaders();
      const response = await fetch(getPreviewUrl(instanceId), { headers });
      if (!response.ok) {
        throw new Error('Failed to load measurement image');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfDialogUrl(blobUrl);
      setPdfDialogType('image');
      setPdfDialogTitle(title || 'Measurement Image');
      setPdfDialogOpen(true);
    } catch (error) {
      console.error('SC image open error:', error);
      toast.error('Unable to open measurement image');
    }
  };

  // Download PDF
  const handleDownloadPdf = async (instanceId, title) => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(getPdfUrl(instanceId), {
        headers
      });
      if (!response.ok) throw new Error('Failed to download PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'worksheet'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('PDF download error:', error);
    }
  };

  // Check for OB measurements from previous step
  useEffect(() => {
    const storedOB = sessionStorage.getItem('obMeasurements');
    if (storedOB) {
      try {
        const parsed = JSON.parse(storedOB);
        setObMeasurements(parsed);
        setShowOBAlert(true);
      } catch (e) {
        console.error('Failed to parse OB measurements:', e);
      }
    }
  }, []);

  const { register, handleSubmit, formState: { errors }, setValue, getValues, trigger, control } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      reportStatus: itemData?.reportData?.reportStatus,
    },
  });

  useEffect(() => {
    if (!currentBranch) return;
    if (itemData?.reportData?._id) {
      dispatch(getReportRequest(itemData.reportData._id));
    }
    dispatch(getStaffList());
  }, [dispatch, currentBranch, itemData]);

  useEffect(() => {
    if (getReportRes?.success) {
      setValue("appointerId", getReportRes.data?.appointerId);
      setValue("diagnosticObjectives", getReportRes.data?.diagnosticObjectives);
      setValue("findings", getReportRes.data?.findings);
      setValue("radiographicImpression", getReportRes.data?.radiographicImpression);
      dispatch(resetGetReport());
    }
  }, [dispatch, getReportRes, setValue]);

  useEffect(() => {
    if (postReportRes?.success || putReportRes?.success) {
      toast.success(postReportRes?.message || putReportRes?.message || 'Report saved');
      dispatch(resetPostReport());

      // If this was a submit (not just save), call onSubmitComplete to navigate away
      if (pendingSubmit && onSubmitComplete) {
        setPendingSubmit(false);
        onSubmitComplete();
      }
    }
  }, [dispatch, postReportRes, putReportRes, pendingSubmit, onSubmitComplete]);

  const insertOBMeasurements = () => {
    if (obMeasurements?.reportText) {
      const currentFindings = getValues('findings') || '';
      const newFindings = obMeasurements.reportText + (currentFindings ? '\n\n' + currentFindings : '');
      setValue('findings', newFindings);
      setShowOBAlert(false);
      sessionStorage.removeItem('obMeasurements');
    }
  };

  const onSubmit = (data) => {
    data.radiologyId = itemData?.radiologyId;
    if (itemData?.reportData?._id) {
      data.reportId = itemData.reportData._id;
      dispatch(putReportRequest(data));
    } else {
      dispatch(postReportRequest(data));
    }
  };

  const handleSave = () => {
    setPendingSubmit(false);
    handleSubmit((data) => onSubmit(data))();
  };

  const handleSubmitReport = () => {
    setValue("reportStatus", "completed");
    setPendingSubmit(true);
    handleSubmit((data) => onSubmit(data))();
  };

  // Handle template application
  const handleApplyTemplate = ({ clinicalNotes, findings, impression }) => {
    if (clinicalNotes) setValue('diagnosticObjectives', clinicalNotes);
    if (findings) setValue('findings', findings);
    if (impression) setValue('radiographicImpression', impression);
  };

  const patientName = itemData?.seletedpatient
    ? `${itemData.seletedpatient.fName} ${itemData.seletedpatient.lName}`
    : 'Unknown Patient';

  return (
    <Box sx={{
      width: '100%',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'grey.100'
    }}>
      {/* Top Bar */}
      <Paper
        elevation={2}
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderRadius: 0,
          zIndex: 10,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={<ImageIcon />}
            label="Report Mode"
            size="small"
            sx={{
              bgcolor: '#1a237e',
              color: '#fff',
              fontWeight: 700,
              '& .MuiChip-icon': { color: '#fff' },
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {patientName}
          </Typography>
          {itemData?.studyData?.orthancStudyDescription && (
            <Typography variant="body2" color="text.secondary">
              {itemData.studyData.orthancStudyDescription}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TemplateSelector
            onApplyTemplate={handleApplyTemplate}
            examList={itemData?.examList}
          />
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
            size="small"
          >
            Save Draft
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSubmitReport}
            disabled={loading}
            size="small"
            color="success"
          >
            Submit Report
          </Button>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Tooltip title="Exit Report Mode">
            <IconButton onClick={onExit} color="default">
              <FullscreenExitIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Main Content - Split View */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left: OHIF Viewer */}
        <Box sx={{
          flex: 1,
          bgcolor: '#000',
          position: 'relative',
          minWidth: 0,
        }}>
          {studyInstanceUID ? (
            <OHIFViewer
              studyInstanceUID={studyInstanceUID}
              embedded={true}
            />
          ) : (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'grey.500'
            }}>
              <Typography>No DICOM study linked</Typography>
            </Box>
          )}
        </Box>

        {/* Right: Report Form */}
        <Paper
          elevation={0}
          sx={{
            width: { xs: '100%', md: 450, lg: 500 },
            display: 'flex',
            flexDirection: 'column',
            borderLeft: 1,
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <Box sx={{
            flex: 1,
            overflow: 'auto',
            p: 3,
          }}>
            {/* Sonographer Worksheet Section */}
            {hasAnyWorksheetContent && (
              <Paper elevation={0} sx={{ mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <Box
                  onClick={() => setWorksheetExpanded(!worksheetExpanded)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    bgcolor: 'grey.50',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon color="primary" fontSize="small" />
                    <Typography variant="body2" fontWeight={600}>
                      Sonographer Worksheet
                    </Typography>
                    {hasPdfs && (
                      <Chip label={`${encapsulatedPDFs.length} PDF`} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                    {hasComments && (
                      <Chip label="Comments" size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                    {hasSCs && (
                      <Chip label={`${secondaryCaptureInstances.length} images`} size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                  </Box>
                  <IconButton size="small">
                    {worksheetExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Box>
                <Collapse in={worksheetExpanded}>
                  <Box sx={{ p: 1.5, pt: 0 }}>
                    {/* PDF List */}
                    {hasPdfs && (
                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                        {encapsulatedPDFs.map((pdf, index) => (
                          <Box
                            key={pdf.instanceId || index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 1,
                              borderRadius: 1,
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flex: 1 }}
                              onClick={() => handleOpenPdfDialog(pdf.instanceId, pdf.documentTitle || `Worksheet ${index + 1}`)}
                            >
                              <PictureAsPdfIcon sx={{ color: 'error.main', fontSize: 20 }} />
                              <Typography variant="body2" fontWeight={500} sx={{ '&:hover': { color: 'primary.main' } }}>
                                {pdf.documentTitle || `Worksheet ${index + 1}`}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.25 }}>
                              <Tooltip title="Open in popup">
                                <IconButton size="small" onClick={() => handleOpenPdfDialog(pdf.instanceId, pdf.documentTitle || `Worksheet ${index + 1}`)}>
                                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download">
                                <IconButton size="small" onClick={() => handleDownloadPdf(pdf.instanceId, pdf.documentTitle)}>
                                  <DownloadIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    )}

                    {/* DICOM Comments */}
                    {hasComments && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="primary" fontWeight={600}>
                          Sonographer Comments
                        </Typography>
                        <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, mt: 0.5 }}>
                          {dicomComments.studyComments && (
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 0.5, fontSize: '0.8rem' }}>
                              {dicomComments.studyComments}
                            </Typography>
                          )}
                          {dicomComments.imageComments?.map((comment, idx) => (
                            <Typography key={idx} variant="body2" sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                              {comment}
                            </Typography>
                          ))}
                          {dicomComments.performingPhysician && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              Sonographer: {dicomComments.performingPhysician}
                            </Typography>
                          )}
                        </Paper>
                      </Box>
                    )}

                    {/* Structured Reports */}
                    {hasSRs && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="primary" fontWeight={600}>
                          Structured Reports ({structuredReports.length})
                        </Typography>
                        {structuredReports.map((sr, idx) => (
                          <Paper key={idx} elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, mt: 0.5 }}>
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                              {sr.seriesDescription}
                            </Typography>
                            {sr.contentText && (
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                                {sr.contentText}
                              </Typography>
                            )}
                          </Paper>
                        ))}
                      </Box>
                    )}

                    {/* Secondary Capture Measurement Images */}
                    {hasSCs && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="primary" fontWeight={600}>
                          Measurement Images ({secondaryCaptureInstances.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {secondaryCaptureInstances.map((sc, idx) => (
                            <Paper
                              key={sc.instanceId || idx}
                              elevation={0}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
                                width: 120,
                              }}
                              onClick={() => {
                                handleOpenScDialog(
                                  sc.instanceId,
                                  sc.imageComments || sc.seriesDescription || `Measurement ${idx + 1}`
                                );
                              }}
                            >
                              {scImageUrls[sc.instanceId] ? (
                                <Box
                                  component="img"
                                  src={scImageUrls[sc.instanceId]}
                                  alt={sc.imageComments || `Measurement ${idx + 1}`}
                                  sx={{ width: '100%', height: 90, objectFit: 'cover', bgcolor: 'grey.200' }}
                                />
                              ) : (
                                <Box sx={{ width: '100%', height: 90, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <Typography variant="caption" color="text.secondary">Loading...</Typography>
                                </Box>
                              )}
                            </Paper>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            )}

            {/* OB Measurements Alert */}
            {showOBAlert && obMeasurements?.reportText && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button color="inherit" size="small" onClick={insertOBMeasurements}>
                      Insert
                    </Button>
                    <Button color="inherit" size="small" onClick={() => setShowOBAlert(false)}>
                      Dismiss
                    </Button>
                  </Box>
                }
              >
                <strong>OB Measurements</strong> - Click Insert to add to findings.
              </Alert>
            )}

            <Stack spacing={3}>
              {/* Clinical Notes - no heading, free-form field */}
              <FormControl fullWidth>
                <Controller
                  name="diagnosticObjectives"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <DictationTextField
                      {...field}
                      placeholder="Clinical notes, history, or objectives..."
                      rows={2}
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1, fontWeight: 600 }}>Findings</FormLabel>
                <Controller
                  name="findings"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <DictationTextField
                      {...field}
                      placeholder="Enter findings or use microphone to dictate"
                      rows={6}
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1, fontWeight: 600 }}>Radiographic Impression</FormLabel>
                <Controller
                  name="radiographicImpression"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <DictationTextField
                      {...field}
                      placeholder="Enter impression or use microphone to dictate"
                      rows={4}
                    />
                  )}
                />
              </FormControl>

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FormLabel sx={{ mb: 1 }}>Appointer</FormLabel>
                    <SelectStaff
                      setUserData={(id) => {
                        setValue("appointerId", id);
                        trigger("appointerId");
                      }}
                      defaultId={getValues()?.appointerId}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FormLabel sx={{ mb: 1 }}>Signature PIN</FormLabel>
                    <OutlinedInput
                      name="appointerSignaturePin"
                      type="password"
                      size="small"
                      autoComplete="new-password"
                      placeholder="Enter PIN to sign"
                      {...register("appointerSignaturePin")}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Stack>
          </Box>
        </Paper>
      </Box>

      {/* PDF Popup Dialog */}
      <Dialog
        open={pdfDialogOpen}
        onClose={() => setPdfDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PictureAsPdfIcon sx={{ color: 'error.main' }} />
            <Typography variant="subtitle1" fontWeight={600}>{pdfDialogTitle}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Open in new tab">
              <IconButton size="small" onClick={() => window.open(pdfDialogUrl, '_blank')}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setPdfDialogOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.900' }}>
          {pdfDialogType === 'image' ? (
            <Box
              component="img"
              src={pdfDialogUrl}
              alt={pdfDialogTitle}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <iframe
              src={pdfDialogUrl}
              title={pdfDialogTitle}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReportModeView;
